import { HgSupportedLanguage, Icon, SupportedPlatforms } from '@smg-real-estate/domus';
import { Category, Country, Listing, OfferType } from '@smg-real-estate/hg-rets-schema';

import {
  isCommercialListing,
  isListingValidForSwissCaution,
  isResidentialListing,
} from '@/components/PartnerIntegrations/functions/helpers';
import {
  getActionLink,
  isListingValidForUbsImmoCheck,
} from '@/components/PartnerIntegrations/UbsImmoCheck/functions';
import { InteractionContainerName } from '@/helpers/GTM/GTMParameters';
import {
  handleISPServiceBoxDynamicLink,
  isListingEligibleForISP,
  SUNRISE_ISP_ID,
} from '@/helpers/ISPIntegration/ISPHelper';
import { isPlatformIs24 } from '@/helpers/platformDetection';

export interface ServiceBoxConfig {
  id: string;
  icon: Icon;
  title: string;
  description: string;
  link: {
    text: string;
    href: string | undefined;
    hrefSkipTranslation?: boolean; // Skip translation for the href
  };
  eventName: string;
  isVisible?: (listing: Listing) => boolean;
}

const MovuServiceBox: ServiceBoxConfig = {
  id: 'movu',
  icon: Icon.MovingBox,
  title: 'serviceBoxes.movu.title',
  description: 'serviceBoxes.movu.description',
  link: {
    text: 'serviceBoxes.movu.cta',
    href: 'serviceBoxes.movu.link',
  },
  eventName: 'movu',
};

const CrifServiceBox: ServiceBoxConfig = {
  id: 'crif',
  icon: isPlatformIs24() ? Icon.DebtCertificateHollow : Icon.DebtCertificate,
  title: 'serviceBoxes.crif.title',
  description: 'serviceBoxes.crif.description',
  link: {
    text: 'serviceBoxes.crif.cta',
    href: 'serviceBoxes.crif.link',
  },
  eventName: 'crif',
};

const IlocatorServiceBox: ServiceBoxConfig = {
  id: 'ilocator',
  icon: Icon.Map,
  title: 'serviceBoxes.ilocator.title',
  description: 'serviceBoxes.ilocator.description',
  link: {
    text: 'serviceBoxes.ilocator.cta',
    href: 'serviceBoxes.ilocator.link',
  },
  eventName: 'iLocator',
};

const ImmocheckServiceBox: ServiceBoxConfig = {
  id: 'immocheck',
  icon: Icon.Immocheck,
  title: 'serviceBoxes.immocheck.title',
  description: 'serviceBoxes.immocheck.description',
  link: {
    text: 'serviceBoxes.immocheck.cta',
    href: '',
    hrefSkipTranslation: true, // Ensure the link is not translated
  },
  eventName: 'ubs_property_valuation',
};

const MortgageAdvisorServiceBox: ServiceBoxConfig = {
  id: 'mortgageAdvisor',
  icon: Icon.Briefcase,
  title: 'serviceBoxes.mortgageAdvisor.title',
  description: 'serviceBoxes.mortgageAdvisor.description',
  link: {
    text: 'serviceBoxes.mortgageAdvisor.cta',
    href: 'serviceBoxes.mortgageAdvisor.link',
  },
  eventName: 'mortgage_advisor',
};

const SwissCautionResidentialServiceBox: ServiceBoxConfig = {
  id: 'swisscaution-residential',
  icon: Icon.Swisscaution,
  title: 'serviceBoxes.swisscaution-residential.title',
  description: 'serviceBoxes.swisscaution-residential.description',
  link: {
    text: 'serviceBoxes.swisscaution-residential.cta',
    href: 'serviceBoxes.swisscaution-residential.link',
  },
  eventName: 'rental_provisional_cert_swisscaution',
  isVisible: (listing: Listing) => {
    return isListingValidForSwissCaution(listing) && isResidentialListing(listing);
  },
};

const SwissCautionCommercialServiceBox: ServiceBoxConfig = {
  id: 'swisscaution-commercial',
  icon: Icon.Swisscaution,
  title: 'serviceBoxes.swisscaution-commercial.title',
  description: 'serviceBoxes.swisscaution-commercial.description',
  link: {
    text: 'serviceBoxes.swisscaution-commercial.cta',
    href: 'serviceBoxes.swisscaution-commercial.link',
  },
  eventName: 'rental_provisional_cert_swisscaution',
  isVisible: (listing: Listing) => {
    return isListingValidForSwissCaution(listing) && isCommercialListing(listing);
  },
};

const ISPPromotionServiceBox: ServiceBoxConfig = {
  id: SUNRISE_ISP_ID,
  icon: Icon.Wifi,
  title: 'sunrise.serviceBox.title',
  description: 'sunrise.serviceBox.description',
  link: {
    text: 'sunrise.serviceBox.cta',
    href: '', // Built dynamically
    hrefSkipTranslation: true, // Don't translate - already a full URL
  },
  eventName: InteractionContainerName.SUNRISE,

  // If the SplitFeatureFlags.KK_1590_SUNRISE_INTEGRATION is on, show the service box.
  isVisible: (listing: Listing, featureFlags?: Record<string, boolean>): boolean => {
    return (
      Boolean(featureFlags?.isSunriseIntegrationEnabled) &&
      isListingEligibleForISP(listing.address?.country as Country, listing.categories as Category[])
    );
  },
};

const serviceBoxesByPlatform = {
  [SupportedPlatforms.hg]: {
    [OfferType.Buy]: [
      MovuServiceBox,
      ImmocheckServiceBox,
      MortgageAdvisorServiceBox,
      ISPPromotionServiceBox,
    ],
    [OfferType.Rent]: [
      MovuServiceBox,
      CrifServiceBox,
      SwissCautionResidentialServiceBox,
      SwissCautionCommercialServiceBox,
      ISPPromotionServiceBox,
      IlocatorServiceBox,
    ],
  },
  [SupportedPlatforms.is24]: {
    [OfferType.Buy]: [MovuServiceBox, ImmocheckServiceBox, MortgageAdvisorServiceBox],
    [OfferType.Rent]: [
      MovuServiceBox,
      CrifServiceBox,
      SwissCautionResidentialServiceBox,
      SwissCautionCommercialServiceBox,
    ],
  },
};

/**
 * Returns the list of service box configurations to display for a given platform, offer type, and listing.
 *
 * Service boxes are filtered, updated with context-specific links, and allow for dynamic visibility based on listing data and optional feature flags.
 * Also handles the dynamic generation of service box links.
 * Boxes may be omitted if not eligible for the current listing.
 *
 * @param platform - The platform (e.g., SupportedPlatforms.hg or SupportedPlatforms.is24).
 * @param listing - The current listing data object.
 * @param locale - The user's language/locale for localized URLs and content.
 * @param featureFlags - (Optional) A map of feature flags to enable/disable specific service boxes based on experimentation or rollout controls.
 * @returns - An array of ServiceBoxConfig objects, ready for rendering in the UI.
 */
export const getServiceBoxes = (
  platform: SupportedPlatforms,
  listing: Listing,
  locale: HgSupportedLanguage,
  featureFlags?: Record<string, boolean>,
): ServiceBoxConfig[] => {
  if (!listing.offerType) {
    return [];
  }

  // Create a deep copy of the serviceBoxes array to avoid mutating the original
  const originalServiceBoxes = serviceBoxesByPlatform[platform][listing.offerType];
  const serviceBoxes = originalServiceBoxes.map((serviceBox) => ({
    ...serviceBox,
    link: { ...serviceBox.link },
  }));

  const newImmocheckLink = prepareImmocheckLink(listing, locale);
  const immocheckIndex = serviceBoxes.findIndex((serviceBox) => serviceBox.id === 'immocheck');
  if (immocheckIndex !== -1) {
    if (newImmocheckLink) {
      const newImmocheckServiceBox: ServiceBoxConfig = {
        ...ImmocheckServiceBox,
        link: {
          text: ImmocheckServiceBox.link.text,
          href: newImmocheckLink,
          hrefSkipTranslation: true, // Ensure the link is not translated
        },
      };
      serviceBoxes.splice(immocheckIndex, 1, newImmocheckServiceBox);
    } else {
      serviceBoxes.splice(immocheckIndex, 1);
    }
  }

  /**
   * Internet Service Provider (ISP) Service Box logic for dynamic link
   */
  const newISPPromotionServiceBox = handleISPServiceBoxDynamicLink(
    ISPPromotionServiceBox,
    serviceBoxes,
    locale,
  );
  if (newISPPromotionServiceBox) {
    serviceBoxes.splice(
      newISPPromotionServiceBox.serviceBoxIndex,
      1,
      newISPPromotionServiceBox.serviceBoxWithDynamicLink,
    );
  }

  return serviceBoxes.filter(
    (serviceBox) =>
      typeof serviceBox.isVisible === 'undefined' || serviceBox.isVisible(listing, featureFlags),
  );
};

function prepareImmocheckLink(listing: Listing, locale: HgSupportedLanguage): string {
  const shouldHideLink = !isListingValidForUbsImmoCheck(listing, locale);
  if (shouldHideLink) return '';

  // Replace old static link with dymanic one:
  return getActionLink(locale as string, listing.id);
}
